import { useEffect, useMemo, useState } from 'react';
import { useProducts } from '@/hooks/useProducts';
import type { Drone } from '@/types/drones';

export type ViewMode = 'grid' | 'list';
export type SortOption = 'nameAsc' | 'nameDesc' | 'newest' | 'oldest';
export type CategoryType = 'all' | 'new' | 'used' | 'buffalo' | 'power-citros' | 'parts';

export type DroneWithType = Drone & { type: 'new' | 'used' | 'buffalo' | 'power-citros' | 'parts' };

const ITEMS_PER_PAGE = 20;

/**
 * Maps category type to translation key
 */
const categoryTranslationMap: Record<
  CategoryType,
  | 'categories.all'
  | 'categories.new'
  | 'categories.used'
  | 'categories.buffalo'
  | 'categories.power-citros'
  | 'categories.parts'
> = {
  all: 'categories.all',
  new: 'categories.new',
  used: 'categories.used',
  buffalo: 'categories.buffalo',
  'power-citros': 'categories.power-citros',
  parts: 'categories.parts',
};

/**
 * Gets the translation key for a given category
 */
export function getCategoryTranslationKey(category: CategoryType) {
  return categoryTranslationMap[category];
}

export interface NavigateOptions {
  category?: CategoryType;
  page?: number;
}

export type NavigateFunction = (options: NavigateOptions) => void;

/**
 * Hook to manage catalog logic: filters, search, sorting, view mode and pagination
 */
export function useCatalog(
  locale: 'pt-BR' | 'en-US' | 'es-ES',
  initialCategory?: CategoryType,
  initialPage?: number,
  navigate?: NavigateFunction
) {
  const [selectedCategory, setSelectedCategory] = useState<CategoryType>(initialCategory || 'all');
  const [currentPage, setCurrentPage] = useState<number>(initialPage || 1);

  // Fetch products from GitHub
  const { products: newDrones } = useProducts('new-drones');
  const { products: usedDrones } = useProducts('used-drones');
  const { products: buffaloLine } = useProducts('buffalo');
  const { products: powerCitros } = useProducts('power-citros');
  const { products: parts } = useProducts('parts');

  // Sync state when initialCategory changes
  useEffect(() => {
    if (initialCategory && initialCategory !== selectedCategory) {
      setSelectedCategory(initialCategory);
      setCurrentPage(1); // Reset to first page when category changes
    }
  }, [initialCategory, selectedCategory]);

  // Sync state when initialPage changes
  useEffect(() => {
    if (initialPage && initialPage !== currentPage) {
      setCurrentPage(initialPage);
    }
  }, [initialPage, currentPage]);

  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [showFilters, setShowFilters] = useState(false);

  // Reset to first page when search query changes
  useEffect(() => {
    if (searchQuery !== '') {
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const allDrones: DroneWithType[] = useMemo(() => {
    const newDronesWithType = newDrones.map((drone) => ({ ...drone, type: 'new' as const }));
    const usedDronesWithType = usedDrones.map((drone) => ({ ...drone, type: 'used' as const }));
    const buffaloWithType = buffaloLine.map((drone) => ({ ...drone, type: 'buffalo' as const }));
    const powerCitrosWithType = powerCitros.map((drone) => ({ ...drone, type: 'power-citros' as const }));
    const partsWithType = parts.map((drone) => ({ ...drone, type: 'parts' as const }));
    return [...newDronesWithType, ...usedDronesWithType, ...buffaloWithType, ...powerCitrosWithType, ...partsWithType];
  }, [newDrones, usedDrones, buffaloLine, powerCitros, parts]);

  const filteredProducts = useMemo(() => {
    return allDrones.filter((drone) => {
      const matchesCategory =
        selectedCategory === 'all' ||
        (selectedCategory === 'new' && drone.type === 'new') ||
        (selectedCategory === 'used' && drone.type === 'used') ||
        (selectedCategory === 'buffalo' && drone.type === 'buffalo') ||
        (selectedCategory === 'power-citros' && drone.type === 'power-citros') ||
        (selectedCategory === 'parts' && drone.type === 'parts');

      const name = drone.name[locale];
      const description = drone.description[locale];
      const matchesSearch =
        searchQuery === '' ||
        name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        description.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [allDrones, selectedCategory, searchQuery, locale]);

  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];

    switch (sortBy) {
      case 'nameAsc':
        return sorted.sort((a, b) => a.name[locale].localeCompare(b.name[locale], locale));
      case 'nameDesc':
        return sorted.sort((a, b) => b.name[locale].localeCompare(a.name[locale], locale));
      case 'newest':
        return sorted.sort((a, b) => {
          const order = { new: 0, used: 1, buffalo: 2, 'power-citros': 3, parts: 4 };
          return order[a.type] - order[b.type];
        });
      case 'oldest':
        return sorted.sort((a, b) => {
          const order = { new: 4, used: 3, buffalo: 2, 'power-citros': 1, parts: 0 };
          return order[a.type] - order[b.type];
        });
      default:
        return sorted;
    }
  }, [filteredProducts, sortBy, locale]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);

  const handleCategoryChange = (category: CategoryType) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    if (navigate) {
      navigate({ category, page: 1 });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (navigate) {
      navigate({ category: selectedCategory, page });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return {
    selectedCategory,
    searchQuery,
    viewMode,
    sortBy,
    showFilters,
    allDrones,
    filteredProducts,
    sortedProducts,
    paginatedProducts,
    currentPage,
    totalPages,
    itemsPerPage: ITEMS_PER_PAGE,
    categoryTranslationKey: getCategoryTranslationKey(selectedCategory),
    setSelectedCategory,
    setSearchQuery,
    setViewMode,
    setSortBy,
    setShowFilters,
    setCurrentPage,
    handleCategoryChange,
    handlePageChange,
  };
}
